
// Convert "HH:MM:SS" or "HH:MM:SS:FF" string to total seconds
export const timeToSeconds = (timeStr: string): number => {
    if (!timeStr) return 0;
    const parts = timeStr.split(':').map(Number);
    // Handle HH:MM:SS and HH:MM:SS:FF
    const h = parts[0] || 0;
    const m = parts[1] || 0;
    const s = parts[2] || 0;
    return (h * 3600) + (m * 60) + s;
};

// Convert total seconds to "HH:MM:SS" string
export const secondsToTime = (totalSeconds: number): string => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
};

// Add two time strings
export const addTimes = (time1: string, duration: string): string => {
    const secs1 = timeToSeconds(time1);
    const secs2 = timeToSeconds(duration);
    return secondsToTime(secs1 + secs2);
};

// Recalculate all subsequent start times in a rundown array
export const recalculateRundownTimes = (items: any[]): any[] => {
    if (items.length === 0) return [];

    const newItems = [...items];

    // The very first item cannot be parallel, so we ensure it's false
    newItems[0] = { ...newItems[0], isParallel: false };

    // We need to keep track of the "nominal start time" of a group
    // and the "max duration" observed in that parallel group.
    let currentGroupStartTime = timeToSeconds(newItems[0].plannedTime || '00:00:00');
    let maxGroupDuration = timeToSeconds(newItems[0].duration || '00:00:00');

    for (let i = 1; i < newItems.length; i++) {
        const item = newItems[i];

        if (item.isParallel) {
            // Parallel: starts at the same time as the group start
            const startTimeStr = secondsToTime(currentGroupStartTime);
            newItems[i] = { ...item, plannedTime: startTimeStr };

            // Update max duration of this group
            const itemDuration = timeToSeconds(item.duration || '00:00:00');
            if (itemDuration > maxGroupDuration) {
                maxGroupDuration = itemDuration;
            }
        } else {
            // Sequential: starts after the PREVIOUS GROUP finishes
            const nextStartSecs = currentGroupStartTime + maxGroupDuration;
            const nextStartStr = secondsToTime(nextStartSecs);

            newItems[i] = { ...item, plannedTime: nextStartStr };

            // Reset group tracking
            currentGroupStartTime = nextStartSecs;
            maxGroupDuration = timeToSeconds(item.duration || '00:00:00');
        }
    }

    return newItems;
};

// Adjust a specific part of the timecode
export const adjustTime = (timeStr: string, amount: number, unit: 'h' | 'm' | 's'): string => {
    // Default to 00:00:00 if empty
    if (!timeStr) timeStr = '00:00:00';

    let secs = timeToSeconds(timeStr);

    if (unit === 'h') secs += amount * 3600;
    if (unit === 'm') secs += amount * 60;
    if (unit === 's') secs += amount;

    // Prevent negative
    if (secs < 0) secs = 0;

    return secondsToTime(secs);
};
